import { FunnelStep, InsightsCampaignRow, InsightsSummary, SourceStamp, StoreTapStatsResponse } from '../../types/marketing';
import DataTable, { DataTableColumn } from './DataTable';
import DailyTapsChart from './DailyTapsChart';
import FunnelBar, { FunnelBarStep } from './FunnelBar';
import { SectionError, SectionSkeleton } from './SectionStatus';
import { formatAed, formatInt, sourceLabel, stepLabel } from './marketingMath';

export const SPEC_079_TOOLTIP = 'Needs app attribution (Spec 079)';

export type TapsState = { status: 'loading' } | { status: 'ok'; data: StoreTapStatsResponse } | { status: 'error'; message: string };

function nullReason(step: FunnelStep, platform: string, stamps: Record<string, SourceStamp>): string {
  if (step.source === 'db' && platform !== 'all') return 'Not split by platform for this step';
  const keys = step.source === 'store' ? ['appStore', 'googlePlay'] : [step.source];
  const stamp = keys.map((k) => stamps[k]).find(Boolean);
  const name = sourceLabel(step.source);
  if (stamp?.status === 'NOT_CONNECTED') return `${name} is not connected`;
  if (stamp?.status === 'FAILED') return `${name} pull failed${stamp.errorCode ? ` (${stamp.errorCode})` : ''}`;
  if (stamp?.status === 'NEVER') return `${name} has not been pulled yet`;
  return 'No data for this range';
}

export function toFunnelSteps(funnel: FunnelStep[], platform: string, stamps: Record<string, SourceStamp>): FunnelBarStep[] {
  return funnel.map((s) => ({
    key: s.step, label: stepLabel(s.step), count: s.count, rateFromPrevious: s.rateFromPrevious,
    byPlatform: platform === 'all' ? s.byPlatform : null, nullReason: s.count == null ? nullReason(s, platform, stamps) : undefined,
  }));
}

const tapRate = (r: InsightsCampaignRow) => (r.taps != null && r.pageLoads ? r.taps / r.pageLoads : null);

export const CAMPAIGN_COLUMNS: DataTableColumn<InsightsCampaignRow>[] = [
  { key: 'campaign', header: 'Campaign', accessor: (r) => r.name || r.campaignKey, format: 'text' },
  { key: 'objective', header: 'Objective', accessor: (r) => r.objective, format: 'text' },
  { key: 'spend', header: 'Spend (AED)', accessor: (r) => r.spend, format: 'aed' },
  { key: 'clicks', header: 'Clicks', accessor: (r) => r.clicks, format: 'int' },
  { key: 'taps', header: 'Taps', accessor: (r) => r.taps, format: 'int' },
  { key: 'pageLoads', header: 'Page loads', accessor: (r) => r.pageLoads, format: 'int' },
  { key: 'tapRate', header: 'Tap rate', accessor: tapRate, format: 'pct' },
  { key: 'installs', header: 'Installs', accessor: (r) => r.installs, format: 'int' },
  { key: 'costPerTap', header: 'Cost per tap (AED)', accessor: (r) => r.costPerTap, format: 'aed' },
  { key: 'costPerInstall', header: 'Cost per install (AED)', accessor: (r) => r.costPerInstall, format: 'aed' },
  { key: 'signUps', header: 'Sign-ups*', accessor: (r) => r.signUps, format: 'int', tooltipWhenNull: SPEC_079_TOOLTIP },
  { key: 'checkouts', header: 'Checkout*', accessor: (r) => r.checkouts, format: 'int', tooltipWhenNull: SPEC_079_TOOLTIP },
  { key: 'paid', header: 'Paid*', accessor: (r) => r.paid, format: 'int', tooltipWhenNull: SPEC_079_TOOLTIP },
  { key: 'costPerSignUp', header: 'Cost per sign-up* (AED)', accessor: (r) => r.costPerSignUp, format: 'aed', tooltipWhenNull: SPEC_079_TOOLTIP },
];

interface Props {
  summary: InsightsSummary;
  taps: TapsState;
  onRetryTaps: () => void;
}

/** Spec 084 Funnel tab — the 9-step funnel, blended cost, taps per day (+ sign-ups, paid) and the per-campaign table. */
export default function FunnelSection({ summary, taps, onRetryTaps }: Props) {
  const { blended } = summary;
  const meta = { from: summary.from, to: summary.to, timezone: summary.timezone };
  const tiles: [string, string, string][] = [
    ['spend', 'Ad spend', formatAed(blended.spend)],
    ['sign-ups', 'Sign-ups', formatInt(blended.signUps)],
    ['cost-per-sign-up', 'Cost per sign-up (blended)', formatAed(blended.costPerSignUp)],
    ['paid-orders', 'Paid orders', formatInt(blended.paidOrders)],
    ['paid-gmv', 'Paid GMV', formatAed(blended.paidGmvAed)],
  ];
  return (
    <div className="mkt-section" data-testid="section-funnel">
      <section className="mkt-card">
        <FunnelBar title="Funnel" steps={toFunnelSteps(summary.funnel, summary.platform, summary.dataAsOf)} testId="funnel-bar" />
      </section>
      <div className="mkt-tiles" data-testid="blended-tiles">
        {tiles.map(([id, label, value]) => (
          <div key={id} className="mkt-tile"><span className="mkt-kpi__label">{label}</span><span className="mkt-tile__value" data-testid={`blended-${id}`}>{value}</span></div>
        ))}
      </div>
      <section className="mkt-card">
        {taps.status === 'loading' && <SectionSkeleton testId="funnel-chart-skeleton" />}
        {taps.status === 'error' && <SectionError title="Couldn't load taps per day" message={taps.message} onRetry={onRetryTaps} testId="funnel-chart-error" />}
        {taps.status === 'ok' && <DailyTapsChart daily={taps.data.daily} extra={summary.daily} />}
      </section>
      <section className="mkt-card">
        <DataTable caption="Campaigns" columns={CAMPAIGN_COLUMNS} rows={summary.byCampaign} rowKey={(r) => r.campaignKey}
          defaultSort={{ key: 'spend', dir: 'desc' }} emptyText="No campaign activity in this range" exportMeta={meta} testId="funnel-campaigns" />
        <p className="mkt-foot">* {SPEC_079_TOOLTIP}: sign-ups, checkouts and paid orders per campaign fill in once the app records where a user came from.</p>
      </section>
    </div>
  );
}
